import { useNavigate } from 'react-router-dom';
import { dwrClassesById } from '@/data/classes';
import type { Character } from '@/schemas/character';
import type { ClassDefinition } from '@/schemas/classDefinition';
import {
  finalizeCharacter,
  type FinalizeCharacterInput,
  type FinalizeCharacterOverrides,
} from '@/services/characterCreation';
import { writeUndoSnapshot } from '@/services/undoSnapshot';
import { saveCharacter, exportCharacterAsJson } from '@/services/characterStorage';
import { useBuilderDraft, useResetBuilder } from '../contexts/useBuilder';
import { areAllSectionsComplete } from '../reducer';
import type { CharacterDraft } from '../types';

interface UseFinalizeBuilderOptions {
  /**
   * **FI-46bis** — Se passato, il finalize è in **modalità edit retroattivo L1**:
   *  - `id` e `createdAt` del Character originale vengono preservati via
   *    `FinalizeCharacterOverrides` (overwrite identity-preserving)
   *  - `writeUndoSnapshot` referenzia il Character originale per restore
   *    completo della cronologia (coerente con FI-46 PR-B)
   *  - NO `resetBuilder()` post-save (in edit-mode niente draft autosaved
   *    da pulire — `persistDraft={false}` lato Provider)
   *
   * In creazione normale (default) → undefined → behavior pre-FI-46bis.
   */
  editFromCharacter?: Character | undefined;
}

interface UseFinalizeBuilderResult {
  /** True quando tutte le 10 sezioni sono complete + il classDef è risolto. */
  canFinalize: boolean;
  /** Finalize completo: persist + reset draft + navigate a `/character`. */
  handleFinalize: () => void;
  /**
   * Variante: finalize + scarica il salvataggio JSON, MA non resetta né
   * naviga (rimane sul builder). Usato dal button "Esporta salvataggio".
   */
  handleExport: () => void;
  classDef: ClassDefinition | undefined;
  draft: CharacterDraft;
}

/**
 * Hook SSOT del finalize del builder. Usato sia dal `<StepNav>` esterno
 * (quando `isLast=true`, click "Finalizza" del top sticky) sia dal
 * `<ReviewPanel>` bottom. Garantisce **comportamento identico** dei due
 * punti di click: l'utente arriva sempre all'Hub in 1 click (memoria
 * `feedback_decision_pay_once`).
 *
 * Prima di PR 11 follow-up: il click "Finalizza" del StepNav scrollava
 * al ReviewPanel; il click "Finalizza" del ReviewPanel persistiva ma
 * non navigava (l'utente doveva poi cliccare "Vai alla scheda"). UX a
 * 3 click rotta.
 */
export function useFinalizeBuilder(
  options: UseFinalizeBuilderOptions = {},
): UseFinalizeBuilderResult {
  const { editFromCharacter } = options;
  const isEditMode = editFromCharacter !== undefined;
  const navigate = useNavigate();
  const draft = useBuilderDraft();
  const resetBuilder = useResetBuilder();
  const classDef = draft.classId ? dwrClassesById[draft.classId] : undefined;
  const canFinalize = areAllSectionsComplete(draft, classDef);

  function handleFinalize(): void {
    if (!classDef || !canFinalize) return;
    const input = buildFinalizeInputFromDraft(draft, classDef);
    if (!input) return;
    // FI-46bis: in edit-mode preserva id + createdAt del PG originale.
    const overrides: FinalizeCharacterOverrides = isEditMode
      ? { id: editFromCharacter.id, createdAt: editFromCharacter.createdAt }
      : {};
    const character = finalizeCharacter(input, overrides);
    if (isEditMode) {
      // FI-46bis: snapshot del PG ORIGINALE pre-edit per restore via UndoToast.
      // Pattern coerente con FI-46 PR-B (LevelUpFlowPage edit-mode).
      writeUndoSnapshot({
        character: editFromCharacter,
        message: 'Modifiche al livello 1 applicate.',
      });
    }
    saveCharacter(character);
    if (!isEditMode) {
      // In creazione normale: pulisci il draft autosaved post-save.
      // In edit-mode: niente da pulire (persistDraft={false} sul Provider).
      resetBuilder();
    }
    void navigate('/character');
  }

  function handleExport(): void {
    if (!classDef || !canFinalize) return;
    const input = buildFinalizeInputFromDraft(draft, classDef);
    if (!input) return;
    const character = finalizeCharacter(input);
    const json = exportCharacterAsJson(character);
    downloadJson(json, `${character.name}-${character.id}.json`);
  }

  return { canFinalize, handleFinalize, handleExport, classDef, draft };
}

/**
 * Estrae i campi richiesti dal `finalizeCharacter` da un draft completo.
 * Restituisce `null` se il draft non ha tutti i campi richiesti
 * (sentinel: il chiamante deve aver già verificato `areAllSectionsComplete`).
 */
function buildFinalizeInputFromDraft(
  draft: CharacterDraft,
  classDef: ClassDefinition,
): FinalizeCharacterInput | null {
  if (!draft.archetypeId || !draft.race || !draft.alignment || !draft.stats || !draft.name) {
    return null;
  }
  return {
    name: draft.name,
    title: draft.title,
    classDef,
    archetypeId: draft.archetypeId,
    race: draft.race,
    alignment: draft.alignment,
    stats: draft.stats,
    chosenAspetti: draft.chosenAspetti,
    moves: draft.moves,
    spells: draft.spells,
    inventory: draft.inventory,
    bonds: draft.bonds,
    bondsDeferred: draft.bondsDeferred,
    notes: draft.notes,
  };
}

/**
 * Helper di download (browser-only). Crea un blob URL e simula un click
 * su un anchor. Niente librerie esterne.
 */
function downloadJson(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
